/* eslint-disable complexity */
import { useContext } from 'preact/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { noop } from '../../helpers/noop';

import Countdown from '../Countdown/Countdown.component';
import PollOptionsList from './pollOptionsList.component';
import PollVideo from './PollVideo.component';
import PollTotalVotes from '../PollTotalVotes/pollTotalVotes.component';
import SubmitButton from '../shared-components/button/SubmitButton';
import SignInGate from '../shared-components/gate/SignInGate.component';

import { PollContext } from '../../polls/poll.component';
import { POLL_TYPES, POLL_VIDEO_TYPES } from '../../helpers/constants';
import { getImageUrl } from '../../helpers/images';
import { getPollTypePartPrefix, joinParts } from '../../helpers/parts';

import FreeTextTriviaInput from './freeTextTriviaInput.component';

import styleSheet from './poll.module.scss';
import { useTranslation } from 'react-i18next';

const styles = styleSheet.locals || {};

const QUESTION_IMG_WIDTH = 712;

/**
 * Component which allows fan to vote for an option
 *
 * @param {object} props poll option props
 * @param {string} props.transitionState transition state
 * @returns {Function} <PollVoteScreen />
 */
function PollVoteScreen({ transitionState }) {
    const {
        poll,
        selectedOptions,
        setSelectedOptions,
        submittingVote = false,
        showVotesCounter = false,
        transitionStyles,
        transitionDelay,
        onVoteSubmit = noop,
        onFreeTextSubmit = noop,
        totalVotes = 0,
        onCountdownEnd,
        isSignedIn,
        isGated
    } = useContext(PollContext);
    const { t } = useTranslation();
    const isTrivia = poll?.type === POLL_TYPES.TRIVIA;
    const isFreeText = poll?.type === POLL_TYPES.FREE_TEXT;
    const isVotingPoll = poll?.type === POLL_TYPES.POLL;
    const pollPartPrefix = getPollTypePartPrefix(poll?.type);

    const questionImgUrl = poll?.questions?.[0]?.question_image_url;

    const isClosed = poll?.state === 'closed';

    const handleInputChange = (event) => {
        setSelectedOptions({
            text: event?.target?.value
        });
    };

    const numAnswers = isTrivia
        ? poll?.questions?.[0]?.correct_options?.length
        : isVotingPoll
          ? poll?.questions?.[0]?.options_selection_limit
          : false;

    const remainingSelections = numAnswers - selectedOptions?.length;

    const isDisabled = () => {
        if (isGated && !isSignedIn) {
            return true;
        }

        if (isFreeText) {
            return (
                selectedOptions?.text?.length === 0 || !selectedOptions?.text
            );
        }

        if (isTrivia) {
            return selectedOptions.length === 0 || remainingSelections !== 0;
        }

        if (isVotingPoll) {
            return remainingSelections !== 0;
        }

        return selectedOptions.length === 0;
    };

    return (
        <div
            className={isGated ? styles.pollWrapperGated : styles.pollWrapper}
            part={joinParts(
                'poll-vote-screen',
                `${pollPartPrefix}-vote-screen`
            )}
        >
            {!isSignedIn && isGated && (
                <SignInGate
                    type={isTrivia || isFreeText ? 'trivia' : 'poll'}
                    isResults={isClosed}
                />
            )}

            <PollVideo poll={poll} />

            {(isTrivia || isFreeText) && questionImgUrl && (
                <div
                    data-cy="questionImage"
                    className={styles.pollQuestionImg}
                    part={joinParts(
                        'poll-question-image',
                        `${pollPartPrefix}-question-image`
                    )}
                >
                    <img
                        src={getImageUrl(questionImgUrl, QUESTION_IMG_WIDTH)}
                        alt={poll?.questions?.[0]?.question_image_text || ''}
                        part={joinParts(
                            'poll-question-image-media',
                            `${pollPartPrefix}-question-image-media`
                        )}
                    />
                </div>
            )}

            {!isFreeText && <PollOptionsList />}

            {isFreeText && <FreeTextTriviaInput onChange={handleInputChange} />}

            <div
                className={classNames(styles.pollSubmitContainer, {
                    [styles.pollSubmitContainerCentered]:
                        isVotingPoll &&
                        poll.poll_video_type === POLL_VIDEO_TYPES.WIDGET
                })}
                style={{
                    ...transitionStyles[transitionState],
                    transitionDelay: `${transitionDelay}ms`
                }}
            >
                {/* option-video polls vote from each card, no global submit */}
                {poll.poll_video_type !== POLL_VIDEO_TYPES.OPTION && (
                    <SubmitButton
                        onSubmit={isFreeText ? onFreeTextSubmit : onVoteSubmit}
                        disabled={isDisabled()}
                        title={t(
                            isTrivia || isFreeText
                                ? 'trivia.submitTooltip'
                                : 'polling.submitTooltip'
                        )}
                        buttonText={t(
                            isTrivia || isFreeText
                                ? 'trivia.submit'
                                : 'polling.submit'
                        )}
                        loading={submittingVote}
                        buttonPart={joinParts(
                            'poll-submit-button',
                            `${pollPartPrefix}-submit-button`
                        )}
                        loadingPart={joinParts(
                            'poll-submit-button-loading',
                            `${pollPartPrefix}-submit-button-loading`
                        )}
                    />
                )}
                {showVotesCounter && (
                    <PollTotalVotes
                        votes={totalVotes}
                        style={{
                            ...transitionStyles[transitionState]
                        }}
                        isTrivia={isTrivia}
                    />
                )}
            </div>
            {poll.closes_at && (
                <Countdown
                    text={t('polling.closesIn')}
                    datetime={poll.closes_at}
                    onCountdownEnd={onCountdownEnd}
                    closes={true}
                />
            )}
        </div>
    );
}

PollVoteScreen.propTypes = {
    transitionState: PropTypes.string.isRequired
};

export default PollVoteScreen;
